<template>
  <span
    v-if="price"
    class="cmp-price-cell ws-text-secondary ws-text-level-up ws-typography ws-typo-xs"
  >
    <!-- OLD PRICE -->
    <span
      v-if="hasMarketingPrice && !transfer"
      class="old-price line-through inline-block"
    >{{ $n(unifiedPrice.marketingPrice, 'currency') }}</span>

    <!-- ACTUAL PRICE -->
    <small :class="{ 'q-ml-sm': hasMarketingPrice }"><strong class="current-price ws-text-primary">{{
      $n(
        (transfer ? unifiedPrice.transferPrice : unifiedPrice.actualPrice) ||
          0,
        'currency'
      )
    }}</strong></small>

    <!-- PERIOD -->
    <span v-if="showPeriod" class="period q-mr-sm">&nbsp;/{{ calcPeriod }}</span>

    <!-- PROLONG PRICE -->
    <div
      v-if="hasProlongPrice"
      class="prolong-price q-mr-sm"
    >
      <span>{{ calcProlongPeriod }}&nbsp;{{
        $n(unifiedPrice.prolongPrice, 'currency')
      }}&nbsp;</span>
      <ws-icon name="info" :size="14" />
      <ws-tooltip placement="bottom">
        {{ $t('cart.prolong_price_hint') }}
      </ws-tooltip>
    </div>
  </span>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { type TranslateResult } from 'vue-i18n';
import { wsClickOutside } from '@loopia-group/utils';
import { WsIcon, WsTooltip } from '@loopia-group/components-vue2';
import type { Price } from '../dc.types';
import type { CartItem } from '@/types';

type CartItemCustomPrice = CartItem['price'] & { prolongPrice: number };

@Component({
  components: {
    WsIcon,
    WsTooltip
  },
  directives: {
    wsClickOutside,
  },
})
export default class PriceCell extends Vue {
  @Prop()
  readonly price!: Price | CartItemCustomPrice;
  @Prop({ type: Number, required: true })
  readonly period!: number;
  @Prop({ type: Boolean, default: true })
  readonly showPeriod!: boolean;
  @Prop(Number)
  readonly prolongPeriod!: number;
  @Prop(Boolean)
  readonly transfer!: boolean;

  get unifiedPrice(): Price {
    if ((this.price as Price).actualPrice) {
      return this.price as Price;
    }
    const price = this.price as CartItemCustomPrice;
    return {
      actualPrice: price.vatExcluded || 0,
      marketingPrice: price.originalPrice ?? null,
      prolongPrice: price.prolongPrice || null,
      transferPrice: 0,
    };
  }

  get hasMarketingPrice() {
    const marketingPrice = this.unifiedPrice.marketingPrice;
    return (
      typeof marketingPrice === 'number' &&
      this.unifiedPrice.actualPrice < marketingPrice
    );
  }

  get hasProlongPrice() {
    const prolongPrice = this.price.prolongPrice;
    return (
      typeof prolongPrice === 'number' &&
      prolongPrice !== this.unifiedPrice.actualPrice
    );
  }

  get calcPeriod(): TranslateResult {
    const period = this.period;
    const periodInMonths = period % 12;
    return this.$t(
      periodInMonths ? 'wsk.general.period.months' : 'wsk.general.period.years',
      {
        period: periodInMonths ? period : period / 12,
        _fallback: 'cart.per_period',
      }
    );
  }

  get calcProlongPeriod(): TranslateResult {
    const period = this.prolongPeriod || this.period;
    const periodInMonths = period % 12;
    return this.$t(
      periodInMonths
        ? 'wsk.general.period.next.months'
        : 'wsk.general.period.next.years',
      {
        period: periodInMonths ? period : period / 12,
        _fallback: 'cart.next_period',
      }
    );
  }
}
</script>

<style lang="scss" scoped>
.cmp-price-cell > .prolong-price {
  display: inline-flex;
  align-items: center;
}
</style>
